import { useTranslation } from 'react-i18next'
import { BarChart3 } from 'lucide-react'

interface TitleLengthBarProps {
  source: number
  avg: number
  mobileCutoff?: number
}

const SCALE = 200
const OPTIMAL = 150
const YELLOW = 160
const RED = 180

function pct(v: number) {
  return Math.min(Math.max((v / SCALE) * 100, 1), 99)
}

export function TitleLengthBar({ source, avg, mobileCutoff = 80 }: TitleLengthBarProps) {
  const { t } = useTranslation()
  const r = (k: string) => t(`amazonListing.report.${k}`)
  const sourceClr = source <= OPTIMAL ? 'text-green-600' : source <= YELLOW ? 'text-yellow-600' : 'text-red-600'

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
      <h4 className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
        <BarChart3 className="w-4 h-4 text-neutral-500" />
        {r('titleLengthComparison')}
      </h4>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">{r('titleLengthTip')}</p>

      <div className="relative mt-10 mb-6">
        {/* Zone bar */}
        <div className="flex h-3 rounded-full overflow-hidden">
          <div className="bg-primary-200 dark:bg-primary-900/40" style={{ width: `${pct(mobileCutoff)}%` }} />
          <div className="bg-green-300 dark:bg-green-800/50" style={{ width: `${pct(OPTIMAL) - pct(mobileCutoff)}%` }} />
          <div className="bg-yellow-300 dark:bg-yellow-800/50" style={{ width: `${pct(RED) - pct(OPTIMAL)}%` }} />
          <div className="bg-red-300 dark:bg-red-800/40 flex-1" />
        </div>

        {/* Mobile cutoff marker */}
        <div className="absolute top-0 h-3 w-px bg-primary-500" style={{ left: `${pct(mobileCutoff)}%` }} />
        <span className="absolute -top-4 text-2xs text-primary-600 dark:text-primary-400 -translate-x-1/2 whitespace-nowrap" style={{ left: `${pct(mobileCutoff)}%` }}>
          {mobileCutoff} Mobile
        </span>

        {/* Optimal marker */}
        <div className="absolute top-0 h-3 w-px bg-green-600 dark:bg-green-400" style={{ left: `${pct(OPTIMAL)}%` }} />
        <span className="absolute -top-4 text-2xs text-green-600 dark:text-green-400 -translate-x-1/2 whitespace-nowrap" style={{ left: `${pct(OPTIMAL)}%` }}>
          {OPTIMAL} Top
        </span>

        {/* Competitor avg */}
        <div className="absolute top-0 h-3 w-0.5 bg-neutral-500 dark:bg-neutral-400" style={{ left: `${pct(avg)}%` }} />
        <span className="absolute top-4 text-2xs text-neutral-500 -translate-x-1/2 whitespace-nowrap" style={{ left: `${pct(avg)}%` }}>
          ø {avg}
        </span>

        {/* Source marker — prominent arrow + pill */}
        <div className="absolute -translate-x-1/2 flex flex-col items-center" style={{ left: `${pct(source)}%`, top: '-28px' }}>
          <span className="px-2 py-0.5 bg-orange-500 text-white rounded-md text-xs font-bold shadow-md whitespace-nowrap ring-2 ring-orange-300 dark:ring-orange-700">
            {source} {r('characters')}
          </span>
          <span className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[6px] border-t-orange-500" />
        </div>
        <div className="absolute top-0 h-3 w-0.5 bg-orange-500 rounded-full shadow-sm" style={{ left: `${pct(source)}%` }} />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-2xs text-neutral-500 dark:text-neutral-400">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-primary-200 dark:bg-primary-800" />{r('titleZoneMobile')}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-300 dark:bg-green-700" />{r('titleZoneOptimal')}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-300 dark:bg-yellow-700" />{r('titleZoneLong')}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-300 dark:bg-red-700" />{r('titleZoneTooLong')}</span>
      </div>

      {/* Source assessment */}
      <div className={`mt-3 text-sm font-medium ${sourceClr}`}>
        {source} {r('characters')} — {source <= OPTIMAL ? r('titleAssessGood') : source <= YELLOW ? r('titleAssessOk') : r('titleAssessTooLong')}
      </div>
    </div>
  )
}
